import { useRef, useState } from "react";
import axios from "axios";
import { Camera, Check, Loader2, X } from "lucide-react";
import { Stars } from "./Stars";
import { imgUrl } from "./useProducts";
import { compressImage } from "@/lib/compressImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = "w-full h-11 px-4 rounded-xl bg-[#ffffff] border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60 transition-colors";
const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : fallback;
};

export const ReviewForm = ({ product, onPosted, onCancel }) => {
  const [step, setStep] = useState("verify");
  const [email, setEmail] = useState("");
  const [ref, setRef] = useState("");
  const [buyer, setBuyer] = useState(null);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/reviews/verify`, { email: email.trim(), order_ref: ref.trim() });
      if (r.data.product_id !== product.id) {
        setError(`That order is for ${r.data.product_name}. Open that piece to review it.`);
      } else if (r.data.already_reviewed) {
        setError("You've already reviewed this order. Thank you!");
      } else {
        setBuyer(r.data);
        setStep("write");
      }
    } catch (err) {
      setError(errText(err, "Could not verify your order."));
    } finally {
      setBusy(false);
    }
  };

  const addPhotos = async (files) => {
    const room = 3 - photos.length;
    const list = Array.from(files).slice(0, room);
    if (!list.length) return;
    setBusy(true);
    setError("");
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("email", email.trim());
        fd.append("order_ref", ref.trim());
        fd.append("file", await compressImage(f));
        const r = await axios.post(`${API}/reviews/upload`, fd, { timeout: 120000 });
        setPhotos((p) => [...p, r.data.url]);
      }
    } catch (err) {
      setError(errText(err, "Photo upload failed."));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return setError("Tap a star rating first.");
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/reviews`, { email: email.trim(), order_ref: ref.trim(), rating, text: text.trim(), photos });
      setStep("done");
      onPosted(r.data);
    } catch (err) {
      setError(errText(err, "Could not post your review."));
    } finally {
      setBusy(false);
    }
  };

  if (step === "done") {
    return (
      <div data-testid="review-posted" className="rounded-2xl bg-[#0a0a0b] text-white p-5 flex items-center gap-3">
        <span className="h-9 w-9 rounded-full bg-[#ff2a85] flex items-center justify-center shrink-0"><Check size={16} strokeWidth={3} /></span>
        <p className="text-sm">JazakAllah khair — your review is live. Deen over Dunya.</p>
      </div>
    );
  }

  return (
    <div data-testid="review-form" className="rounded-2xl border border-[#e3e2dc] bg-[#faf9f6] p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display font-extrabold uppercase text-sm">Write a review</p>
          <p className="text-xs text-[#5f5e5a] mt-1">{step === "verify" ? "Verified buyers only — enter the email and order number from your confirmation email." : `Reviewing as ${buyer.reviewer} · Verified buyer`}</p>
        </div>
        <button type="button" data-testid="review-cancel" onClick={onCancel} aria-label="Close" className="text-[#5f5e5a] hover:text-[#ff2a85]"><X size={16} /></button>
      </div>

      {step === "verify" ? (
        <form onSubmit={verify} className="mt-4 grid sm:grid-cols-2 gap-3">
          <input data-testid="review-email" className={input} required type="email" placeholder="Email used at checkout" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="review-ref" className={`${input} font-mono-d uppercase tracking-[0.15em]`} required minLength={6} maxLength={12} placeholder="Order no. e.g. A1B2C3D4" value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} />
          {error && <p data-testid="review-error" className="sm:col-span-2 text-xs text-[#ff2a85] font-mono-d uppercase tracking-wider">{error}</p>}
          <button data-testid="review-verify" type="submit" disabled={busy} className="sm:col-span-2 h-11 rounded-full bg-[#0a0a0b] hover:bg-[#ff2a85] text-white font-mono-d text-[10px] uppercase tracking-[0.2em] disabled:opacity-60 flex items-center justify-center gap-2 transition-colors">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Verify my order"}
          </button>
        </form>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="flex items-center gap-3">
            <span className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#5f5e5a]">Your rating</span>
            <Stars value={rating} size={22} onChange={setRating} testId="review-rating" />
          </div>
          <div>
            <textarea data-testid="review-text" className={`${input} h-28 py-3 resize-none`} required minLength={3} maxLength={400} placeholder="How does it fit? How's the quality? Keep it short and honest." value={text} onChange={(e) => setText(e.target.value)} />
            <p className="text-right font-mono-d text-[9px] text-[#5f5e5a] mt-1">{text.length}/400</p>
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              {photos.map((p) => (
                <span key={p} className="relative h-16 w-16 rounded-lg overflow-hidden border border-[#e3e2dc]">
                  <img src={imgUrl(p)} alt="" className="h-full w-full object-cover" />
                  <button type="button" data-testid="review-photo-remove" onClick={() => setPhotos((l) => l.filter((x) => x !== p))} aria-label="Remove photo" className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-[#0a0a0b]/80 text-white flex items-center justify-center"><X size={10} /></button>
                </span>
              ))}
              {photos.length < 3 && (
                <button type="button" data-testid="review-add-photo" disabled={busy} onClick={() => fileRef.current?.click()} className="h-16 w-16 rounded-lg border border-dashed border-[#c9c8c2] hover:border-[#ff2a85] text-[#5f5e5a] hover:text-[#ff2a85] flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-60">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Camera size={16} />}
                  <span className="font-mono-d text-[8px] uppercase tracking-[0.15em]">Photo</span>
                </button>
              )}
              <input ref={fileRef} data-testid="review-photo-input" type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
            </div>
            <p className="text-[11px] text-[#5f5e5a] mt-2">Up to 3 photos · JPG, PNG or HEIC</p>
          </div>
          {error && <p data-testid="review-error" className="text-xs text-[#ff2a85] font-mono-d uppercase tracking-wider">{error}</p>}
          <button data-testid="review-submit" type="submit" disabled={busy} className="w-full h-11 rounded-full bg-[#ff2a85] hover:bg-[#e60067] text-white font-mono-d text-[10px] uppercase tracking-[0.2em] disabled:opacity-60 flex items-center justify-center gap-2 transition-colors">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Post review"}
          </button>
        </form>
      )}
    </div>
  );
};
